// src/screens/LibraryScreen.js
//
// The differentiator screen: users import their own tracks (local files in
// this prototype — wire up a streaming SDK later if you want Spotify/Apple
// Music support) and tag each one with categories. Every other screen reads
// from this same library, so users stop hunting through separate apps.

import React, { useState } from 'react';
import { View, Text, FlatList, Pressable, StyleSheet, TextInput, ImageBackground, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLibrary } from '../context/LibraryContext';
import { AudioEngine } from '../audio/AudioEngine';
import { Colors, Radius, Typography, Fonts } from '../theme';

export default function LibraryScreen() {
  const {
    tracks,
    categories,
    importTrack,
    reimportTrack,
    removeTrack,
    setTrackCategories,
    addCategory,
    editTrackArtwork,
  } = useLibrary();
  const [activeFilter, setActiveFilter] = useState('All');
  const [newCategoryText, setNewCategoryText] = useState('');
  const [playingId, setPlayingId] = useState(null);

  const visibleTracks =
    activeFilter === 'All'
      ? tracks
      : tracks.filter((t) => t.categories.includes(activeFilter));

  const toggleCategoryOnTrack = (track, category) => {
    const has = track.categories.includes(category);
    const next = has
      ? track.categories.filter((c) => c !== category)
      : [...track.categories, category];
    setTrackCategories(track.id, next);
  };

  const togglePlay = async (track) => {
    if (track.needsReimport) {
      Alert.alert(
        'File not found',
        `"${track.name}" can't be found — it may have been moved or deleted. Tap Re-import to locate it again.`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Re-import',
            onPress: async () => {
              const updated = await reimportTrack(track.id);
              if (!updated) return; // user cancelled picker
              // Auto-play the freshly imported track
              if (playingId) await AudioEngine.stopLayer(`library-${playingId}`);
              await AudioEngine.playLayer(`library-${updated.id}`, {
                uri: updated.uri,
                name: updated.name,
                volume: 0.8,
                artworkUri: updated.artworkUri,
              });
              setPlayingId(updated.id);
            },
          },
        ]
      );
      return;
    }

    if (playingId === track.id) {
      await AudioEngine.stopLayer(`library-${track.id}`);
      setPlayingId(null);
    } else {
      if (playingId) await AudioEngine.stopLayer(`library-${playingId}`);
      await AudioEngine.playLayer(`library-${track.id}`, {
        uri: track.uri,
        name: track.name,
        volume: 0.8,
        artworkUri: track.artworkUri,
      });
      setPlayingId(track.id);
    }
  };

  return (
    <ImageBackground
      source={require('../../assets/backgrounds/bg_music.jpg')}
      style={styles.screen}
      imageStyle={styles.bgImage}
    >
      <Text style={styles.subheader}>
        Import your own tracks and sort them once — find them by mood instead
        of searching your music app every night.
      </Text>

      <Pressable style={styles.importButton} onPress={importTrack}>
        <Ionicons name="add-circle" size={18} color={Colors.textOnAccent} />
        <Text style={styles.importText}>Add music</Text>
      </Pressable>
      <Text style={styles.importHint}>
        Opens your device's file picker — includes iCloud Drive, Google Drive,
        or Dropbox if you have those apps installed.
      </Text>
      <Text style={styles.importNote}>
        Works with music files you own or have downloaded. Tracks streamed
        through Spotify, Apple Music, or Pandora can't be imported — play
        those in their own app alongside SleepMixBlueprint instead.
      </Text>

      <Text style={styles.helpPointer}>
        Can't find your song? Check the Help tab for a step-by-step guide.
      </Text>

      <View style={styles.filterRow}>
        {['All', ...categories].map((c) => (
          <Pressable
            key={c}
            onPress={() => setActiveFilter(c)}
            style={[styles.filterPill, activeFilter === c && styles.filterPillActive]}
          >
            <Text style={[styles.filterText, activeFilter === c && styles.filterTextActive]}>
              {c}
            </Text>
          </Pressable>
        ))}
      </View>

      <View style={styles.newCategoryRow}>
        <TextInput
          style={styles.newCategoryInput}
          placeholder="New category (e.g. Rainy Nights)"
          placeholderTextColor={Colors.textMuted}
          value={newCategoryText}
          onChangeText={setNewCategoryText}
          onSubmitEditing={() => {
            if (newCategoryText.trim()) {
              addCategory(newCategoryText.trim());
              setNewCategoryText('');
            }
          }}
        />
      </View>

      <FlatList
        data={visibleTracks}
        keyExtractor={(t) => t.id}
        contentContainerStyle={{ paddingBottom: 40 }}
        ListEmptyComponent={
          <Text style={styles.empty}>
            No tracks yet — add some music above to build your sleep/study library.
          </Text>
        }
        renderItem={({ item }) => (
          <View style={[styles.trackCard, item.needsReimport && styles.trackCardMissing]}>
            <View style={styles.trackRow}>
              <Pressable onPress={() => togglePlay(item)} style={[styles.playButton, item.needsReimport && styles.playButtonMissing]}>
                <Ionicons
                  name={item.needsReimport ? 'warning' : (playingId === item.id ? 'pause' : 'play')}
                  size={18}
                  color={item.needsReimport ? Colors.warning : Colors.textOnAccent}
                />
              </Pressable>
              <View style={styles.trackNameCol}>
                <Text style={[styles.trackName, item.needsReimport && styles.trackNameMissing]} numberOfLines={1}>
                  {item.name}
                </Text>
                {item.needsReimport && (
                  <Text style={styles.reimportHint}>File not found — tap to re-import</Text>
                )}
              </View>
              <Pressable
                onPress={() => editTrackArtwork(item.id)}
                style={styles.artworkButton}
                accessibilityLabel={item.artworkUri ? 'Edit cover art' : 'Add cover art'}
              >
                <Ionicons
                  name={item.artworkUri ? 'image' : 'image-outline'}
                  size={18}
                  color={item.artworkUri ? Colors.accent : Colors.textMuted}
                />
              </Pressable>
              <Pressable onPress={() => removeTrack(item.id)}>
                <Ionicons name="trash-outline" size={18} color={Colors.textMuted} />
              </Pressable>
            </View>
            <View style={styles.tagRow}>
              {categories.map((c) => {
                const active = item.categories.includes(c);
                return (
                  <Pressable
                    key={c}
                    onPress={() => toggleCategoryOnTrack(item, c)}
                    style={[styles.tag, active && styles.tagActive]}
                  >
                    <Text style={[styles.tagText, active && styles.tagTextActive]}>
                      {c}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        )}
      />
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  screen:    { flex: 1, backgroundColor: Colors.bg, padding: 16 },
  bgImage:   { opacity: 0.18 },
  subheader: { color: Colors.textSecondary, ...Typography.caption, marginBottom: 14 },

  importButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Colors.accent,
    borderRadius: Radius.md,
    paddingVertical: 12,
    marginBottom: 16,
  },
  importText: { color: Colors.textOnAccent, fontFamily: Fonts.bold },
  importHint:   { color: Colors.textMuted, ...Typography.small, marginBottom: 6, marginTop: -8 },
  importNote:   { color: Colors.textMuted, ...Typography.small, marginBottom: 6, fontStyle: 'italic' },
  helpPointer:  { color: Colors.accent, ...Typography.small, marginBottom: 14 },

  filterRow:        { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  filterPill:       { paddingVertical: 6, paddingHorizontal: 12, borderRadius: Radius.pill, backgroundColor: Colors.surface },
  filterPillActive: { backgroundColor: Colors.accent },
  filterText:       { color: Colors.textSecondary, ...Typography.small },
  filterTextActive: { color: Colors.textOnAccent, fontFamily: Fonts.bold },

  newCategoryRow: { marginBottom: 14 },
  newCategoryInput: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.sm,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: Colors.textPrimary,
    ...Typography.caption,
  },

  empty: { color: Colors.textMuted, textAlign: 'center', marginTop: 40, ...Typography.caption },

  trackCard: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.lg,
    padding: 12,
    marginBottom: 10,
  },
  trackCardMissing: {
    opacity: 0.7,
    borderWidth: 1,
    borderColor: Colors.warning,
  },
  trackRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  playButton: {
    width: 32,
    height: 32,
    borderRadius: Radius.full,
    backgroundColor: Colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  playButtonMissing: {
    backgroundColor: Colors.surface2,
  },
  trackNameCol: { flex: 1, justifyContent: 'center' },
  trackName: { color: Colors.textPrimary, ...Typography.body },
  trackNameMissing: { color: Colors.textMuted },
  reimportHint: {
    color: Colors.warning,
    ...Typography.small,
    marginTop: 2,
  },

  artworkButton: { padding: 4, marginRight: 8 },

  tagRow:        { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  tag:           { paddingVertical: 4, paddingHorizontal: 10, borderRadius: Radius.pill, backgroundColor: Colors.surface2 },
  tagActive:     { backgroundColor: Colors.accentDim },
  tagText:       { color: Colors.textMuted, ...Typography.small },
  tagTextActive: { color: Colors.textBright, fontFamily: Fonts.semiBold },
});
